import { useState, useEffect, useRef } from "react";
import {
  cn,
  Spinner,
  TrafficBadge,
  Link,
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
  Icon,
  Heading,
} from "@ogt/ui";

import { buildSpaUrl } from "src/config/urls";

interface Departure {
  LineNrReal: number;
  RunNo: number;
  IsDeparture: boolean;
  OgtTimeUntilDepOrArr: string;
  LineName?: string;
  Towards?: string;
  StopName?: string;
  StopPointId?: string;
  StopPointAlias?: string;
  StopPointAliasId?: string;
  Lat?: number;
  Lon?: number;
  LineTypeName: string;
  TrafficTypeGroupCssClass: string;
  iconClass: string;
  IconReplaceLineName: string;
  RouteLinkKey: string;
  PassingStops: number[];
  RealTime: {
    RealTimeInfo: {
      DepTimeDeviation: number;
      DepDeviationAffect: number;
      ArrTimeDeviation: number;
      ArrDeviationAffect: number;
      Canceled: boolean;
      ArrCanceled: boolean;
      DepCanceled: boolean;
    };
  };
}

interface DepartureBoardProps {
  stopName: string;
  stopId: number;
  onDepartureCountChange?: (count: number) => void;
}

type TrafficMeta = Parameters<typeof TrafficBadge>[0]["meta"];

function toTrafficMetaFromDeparture(dep: Departure): TrafficMeta {
  return {
    iconCss: dep.iconClass,
    groupCss: dep.TrafficTypeGroupCssClass,
    title: dep.LineName ?? "",
  };
}

export const DepartureBoard = ({
  stopId,
  stopName,
  onDepartureCountChange,
}: DepartureBoardProps) => {
  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";

  const [depArrs, setDepArrs] = useState<Departure[]>([]);
  const stopIdRef = useRef<number | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [passingStopsMap, setPassingStopsMap] = useState<Record<string, any[]>>(
    {}
  );
  //const [loading, setLoading] = useState<boolean>(false);
  const [hasFetchedDepartures, setHasFetchedDepartures] = useState(false);
  const [initialLoadingDepartures, setInitialLoadingDepartures] =
    useState(false);
  const [passingStopsLoading, setPassingStopsLoading] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (!stopId) return;

    stopIdRef.current = stopId;
    fetchDeparturesAndArrivals(stopId, true);

    const interval = setInterval(() => {
      if (stopIdRef.current !== null) {
        fetchDeparturesAndArrivals(stopIdRef.current);
      }
    }, 5000);

    return () => clearInterval(interval); // cleanup
  }, [stopId]);

  const fetchPassingStops = async (routeLinkKey: string) => {
    try {
      setPassingStopsLoading((prev) => ({ ...prev, [routeLinkKey]: true }));

      const res = await fetch(
        `${apiBaseUrl}/api/v1/Routes/${routeLinkKey}?onlyFuture=false`
      );
      const data = await res.json();

      setPassingStopsMap((prev) => ({
        ...prev,
        [routeLinkKey]: data,
      }));
    } catch (err) {
      console.error("Failed to fetch passing stops", err);
    } finally {
      setPassingStopsLoading((prev) => ({ ...prev, [routeLinkKey]: false }));
    }
  };

  const fetchDeparturesAndArrivals = (stopId: number, isInitial = false) => {
    if (isInitial) {
      setInitialLoadingDepartures(true);
      setHasFetchedDepartures(true);
    }

    const params = new URLSearchParams({
      stopAreaId: stopId.toString(),
      date: "", // current time
      delay: "0",
      maxNumberOfResultPerColumn: "10",
      columnsPerPageCount: "1",
      pagesCount: "1",
      lines: "",
      trafficTypes: "",
      stopPoints: "",
      sortOrder: "DepartureTime",
      useDaySeparator: "false",
    });

    fetch(`${apiBaseUrl}/api/v1/StopAreas/Departures?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.groups?.length) {
          console.warn("No departures found for stop:", stopId);
          setDepArrs([]);

          if (isInitial) {
            onDepartureCountChange?.(0);
          }

          return;
        }

        const departures: Departure[] = [];

        data.groups.forEach((group: any[]) => {
          group.forEach((entry: any) => {
            const line = entry?.Line;
            if (!line) return;

            const stopPointData = line.OgtStopPointData ?? {};

            departures.push({
              LineNrReal: line.LineNrReal,
              RunNo: line.RunNo,
              IsDeparture: line.IsDeparture,
              OgtTimeUntilDepOrArr: line.OgtTimeUntilDepOrArr,
              LineName: line.LineName,
              Towards: line.Towards,
              StopName: line.StopPointAliasFormatted,
              StopPointId: stopPointData.stopPointId ?? null,
              StopPointAlias: stopPointData.stopPointAlias ?? null,
              StopPointAliasId: stopPointData.stopPointAliasId ?? null,
              Lat: stopPointData.ll?.[0] ?? null,
              Lon: stopPointData.ll?.[1] ?? null,
              LineTypeName: line.LineTypeName,
              TrafficTypeGroupCssClass: line.Ogt.OgtTrafficTypeGroupCssClass,
              IconReplaceLineName: line.Ogt.OgtTrafficTypeIconReplaceLineName,
              iconClass: line.Ogt.OgtTrafficTypeIconCssClass,
              RouteLinkKey: line.RouteLinkKey ?? "",
              PassingStops: line.Ogt?.PassingStops ?? [],
              RealTime: {
                RealTimeInfo: {
                  DepTimeDeviation: line.RealTimeInfo?.DepTimeDeviation ?? 0,
                  DepDeviationAffect:
                    line.RealTimeInfo?.DepDeviationAffect ?? 0,
                  ArrTimeDeviation: line.RealTimeInfo?.ArrTimeDeviation ?? 0,
                  ArrDeviationAffect:
                    line.RealTimeInfo?.ArrDeviationAffect ?? 0,
                  Canceled:
                    line.IsCanceled ?? line.RealTimeInfo?.Canceled ?? false,
                  ArrCanceled: line.RealTimeInfo?.ArrCanceled ?? false,
                  DepCanceled: line.RealTimeInfo?.DepCanceled ?? false,
                },
              },
            });
          });
        });

        const visibleDepartures = departures.filter(
          (entry) =>
            entry.IsDeparture &&
            !String(entry.OgtTimeUntilDepOrArr || "")
              .toLowerCase()
              .includes("avgått")
        );

        setDepArrs(departures);

        if (isInitial) {
          onDepartureCountChange?.(visibleDepartures.length);
        }
      })
      .catch((err) => {
        console.error("Error fetching new departures:", err);
      })
      .finally(() => {
        if (isInitial) setInitialLoadingDepartures(false);
      });
  };
  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("sv-SE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  function toLineDisplayFromLineNrReal(lineNrReal: number) {
    const suffix = lineNrReal % 10000;
    return String(suffix);
  }

  function getLineForMap(entry: Departure) {
    if (typeof entry.LineNrReal === "number") {
      return toLineDisplayFromLineNrReal(entry.LineNrReal);
    }
    return (entry.LineName ?? "").trim();
  }

  function buildLiveMapUrl(lineNameOrNumber: string) {
    return buildSpaUrl("map", {
      live: lineNameOrNumber,
      linje: lineNameOrNumber,
      aktivLinje: lineNameOrNumber,
    });
  }

  const filtered = depArrs.filter(
    (entry) =>
      entry.IsDeparture &&
      !String(entry.OgtTimeUntilDepOrArr || "")
        .toLowerCase()
        .includes("avgått")
  );

  function getDepartureAriaLabel(entry: Departure) {
    const timeText = String(entry.OgtTimeUntilDepOrArr ?? "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const destination = entry.Towards ?? "";
    const line = entry.LineName ?? "";
    const stopPoint = entry.StopPointAliasId ?? "";

    const parts = [`Linje ${line}`, `mot ${destination}`];

    if (stopPoint) {
      parts.push(`Läge ${stopPoint.toUpperCase()}`);
    }

    if (entry.RealTime?.RealTimeInfo?.Canceled) {
      parts.push("Inställd");
      return `${parts.join(". ")}.`;
    }

    const isClockTime = /^\d{1,2}:\d{2}$/.test(timeText);

    if (isClockTime) {
      parts.push(`Avgår klockan ${timeText}`);
    } else if (timeText) {
      parts.push(`Avgår om ${timeText}`);
    } else {
      parts.push("Avgångstid saknas");
    }

    return `${parts.join(". ")}.`;
  }

  return (
    <div className="departure-board">
      {!hasFetchedDepartures ? null : initialLoadingDepartures ? (
        <div className="spinner-container">
          <div className="spinner">
            <Spinner label="Laddar avgångar..." />
          </div>
        </div>
      ) : (
        <>
          {filtered.length > 0 && (
            <>
              <Heading as="h4" className="mb-2">
                Kommande avgångar från {stopName}
              </Heading>

              <div className="flex flex-col w-full">
                <Accordion
                  allowAllClosed
                  type="single"
                  value={expandedKey ?? undefined}
                  onValueChange={(val) => {
                    setExpandedKey(val ?? null);
                    if (val && !passingStopsMap[val]) fetchPassingStops(val);
                  }}
                >
                  {filtered.map((entry, index) => {
                    const routeLinkKey = entry.RouteLinkKey;
                    const itemValue = routeLinkKey || `departure-${index}`;

                    const passingStops = passingStopsMap[itemValue] ?? [];
                    const visibleStops = passingStops.filter((stop) => {
                      const time =
                        stop.OgtTimeUntilDeparture?.toLowerCase() ?? "";
                      return !time.includes("avgått");
                    });

                    const isPendeln =
                      entry.LineName?.includes("Östgötapendeln");
                    const showText = !entry.IconReplaceLineName;

                    const lineForMap = getLineForMap(entry);
                    const mapUrl = lineForMap
                      ? buildLiveMapUrl(lineForMap)
                      : undefined;

                    return (
                      <AccordionItem
                        key={`${entry.RouteLinkKey}-${entry.RunNo}`}
                        value={itemValue}
                      >
                        <AccordionTrigger
                          aria-label={getDepartureAriaLabel(entry)}
                          className=" flex flex-row md:flex-row items-start md:items-center relative 
                          [&>svg:last-child]:absolute
                          [&>svg:last-child]:right-4
                          [&>svg:last-child]:top-1/2
                          [&>svg:last-child]:-translate-y-1/2"
                        >
                          <div className="flex flex-col md:flex-row flex-1 md:items-center">

                          <div className="flex md:justify-start flex-row md:flex-row flex-1 items-center">
                              <div>
                                <TrafficBadge
                                  aria-hidden="true"
                                  meta={toTrafficMetaFromDeparture(entry)}
                                  className={cn(
                                    entry.TrafficTypeGroupCssClass,
                                    entry.LineTypeName,
                                    isPendeln && "pendeln"
                                  )}
                                  titleOverride={entry.LineName}
                                >
                                  {showText ? entry.LineName : null}
                                </TrafficBadge>
                              </div>

                              <span className="ml-4 flex-1 w-full" aria-hidden="true">
                                mot {entry.Towards}
                              </span>
                            </div>

                            <div className="flex-1 mt-3 md:mt-0" aria-hidden="true">
                              Läge {entry.StopPointAliasId?.toUpperCase()}
                            </div>

                            {entry.RealTime?.RealTimeInfo?.Canceled ? (
                              <div className="canceled-sticker flex-1 mt-3 md:mt-0">
                                Inställd
                              </div>
                            ) : (
                              <div className="flex-1 flex gap-2 mt-3 md:mt-0">
                                <div
                                  aria-hidden="true"
                                  dangerouslySetInnerHTML={{
                                    __html: entry.OgtTimeUntilDepOrArr,
                                  }}
                                />
                                <Icon name="radio" aria-hidden="true" />
                              </div>
                            )}
                          </div>
                        </AccordionTrigger>

                        <AccordionContent>
                          <div className="lines-passing-container">
                            {passingStopsLoading[itemValue] ? (
                              <div className="p-4 flex">
                                <Spinner
                                  className="flex-row"
                                  size={16}
                                  label="Laddar hållplatser..."
                                />
                              </div>
                            ) : visibleStops.length > 0 ? (
                              <div className="flex flex-col justify-between">
                                <ul className="line-stops-list">
                                  {visibleStops.map((stop, i) => (
                                    <li key={i}>
                                      <span className="dep-time">
                                        {formatTime(stop.DepDateTime)}
                                      </span>
                                      <span className="dot-container">
                                        <div className="dot"></div>
                                        <div className="line"></div>
                                      </span>
                                      <span className="dep-name">
                                        {stop.Place.PlaceName}
                                      </span>
                                      {stop.RealTime?.[0]?.DepCanceled && (
                                        <span className="text-red-600 ml-4">
                                          (Inställd)
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>

                                <div className="mt-4">
                                  <Link
                                    href={mapUrl}
                                    target="_blank"
                                    format="box"
                                    boxVariant="primary"
                                    leftIcon="radio"
                                    rightIcon="chevron-right"
                                    rel="noopener noreferrer"
                                    underline="none"
                                    variant="default"
                                    size="lg"
                                    iconPosition="right"
                                  >
                                    Visa buss på kartan
                                  </Link>
                                </div>
                              </div>
                            ) : (
                              <em>Inga kommande avgångar.</em>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
